import { Badge, Text, Box, Stack, Link, HStack, Table, useDisclosure, Center, VStack } from "@chakra-ui/react"
import { useState, useEffect } from "react";
import { InfoResponse } from "../api/InfoResponse";
import { infoService } from '../service/InfoService';
import { deLocaleOption, parseIso } from "src/data/DateTimeUtil";
import { differenceInSeconds, formatDuration, intervalToDuration, setDefaultOptions } from "date-fns";
import { de } from 'date-fns/locale';
import packageJson from '../../package.json';
import apiClient from "src/service/core/ApiClient";
import ColorPicker from "src/components/ColorPicker";
import { OrgUtil } from "src/data/OrgUtil";
import { ProgressBarReact } from "src/components/ProgressBar";
import { useAuthContext } from "src/auth/AuthContext";
import RoleBadge from "src/components/RoleBadge";
import ExchangeRates from "src/components/ExchangeRates";
import { ChakraDatePickerCompat } from "@/components/fields/DateSelect";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import SeparatorText from "@/components/SeparatorText";
import YearSelect from "@/components/fields/YearSelect";
import { Alert } from "@/components/ui/alert";
import { Slider } from "@/components/ui/slider";

const Info = () => {
    const { user } = useAuthContext();
    const [info, setInfo] = useState<InfoResponse>(null);
    const { open, onOpen, onClose } = useDisclosure();
    const isRoot = user?.data?.role && OrgUtil.isRoleRoot(user.data.role);

    // test date picker
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    // test year select/picker
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [progressValue, setProgressValue] = useState(50);

    setDefaultOptions(deLocaleOption);

    const uptimeTime = (i: InfoResponse): string => {
        let serverNow = parseIso(i.currentTime);
        let seconds = differenceInSeconds(serverNow, parseIso(i.startTime))
        const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
        return formatDuration(duration, { format: ['months', 'days', 'hours', 'minutes', 'seconds'] })
    }

    useEffect(() => {
        infoService.getInfo().then((res: InfoResponse) => setInfo(res));
    }, []);

    return (
        <Stack as={Box} p='.5rem' direction={{ base: 'column' }}>
            <Table.Root striped colorPalette='teal' size='sm' align={'left'} w={{ base: '100%', lg: '50%' }}>
                <Table.Body>
                    <Table.Row>
                        <Table.Cell width='25%'><Text as='b'>User:</Text></Table.Cell>
                        <Table.Cell>
                            {user && (
                                <HStack ml='5'>
                                    <Text fontWeight='bold'>{user?.data?.firstName}</Text>
                                    <RoleBadge role={user?.data?.role} />
                                </HStack>
                            )}
                            {!user && (
                                <HStack ml='5'>
                                    <Badge variant='outline' colorScheme='red'>None</Badge>
                                </HStack>
                            )}
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Organization:</Text></Table.Cell>
                        <Table.Cell>
                            <Badge ml='5' mr='10' variant='surface' colorPalette='yellow'>{OrgUtil.orgName(user?.data)}</Badge>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Status:</Text></Table.Cell>
                        <Table.Cell>
                            <Badge ml='5' mr={10} variant='surface' colorPalette={info ? 'green' : 'red'}>{info ? 'Pong' : 'Ping'}</Badge>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Database:</Text></Table.Cell>
                        <Table.Cell>
                            <Badge ml='5' mr={10} variant='surface' colorPalette={(info?.dbAlive ?? false) ? 'green' : 'red'}>{(info?.dbAlive ?? false) ? 'Alive' : 'Checking'}</Badge>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Build Time:</Text></Table.Cell>
                        <Table.Cell><Text ml='5' as='i'>{parseIso(info?.buildTime ?? new Date()).toLocaleString() ?? '?'}</Text></Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Server Time:</Text></Table.Cell>
                        <Table.Cell>
                            <Text ml='5' as='i'>{parseIso(info?.currentTime ?? new Date())?.toLocaleString() ?? '?'}</Text>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Uptime:</Text></Table.Cell>
                        <Table.Cell>
                            <Text ml='5' as='i'>{info ? uptimeTime(info) : 'n/a'}</Text>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Memory Usage:</Text></Table.Cell>
                        <Table.Cell>
                            <Box ml='5' w='12rem'>{ProgressBarReact((info?.memoryUsedPercentile ?? 0) / 100)}</Box>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Frontend Version:</Text></Table.Cell>
                        <Table.Cell>
                            <Text ml='5' as='i'>{packageJson.version}</Text>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>API Host:</Text></Table.Cell>
                        <Table.Cell>
                            <Text ml='5' as='i'>{apiClient.baseUrl}</Text>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>OpenAPI:</Text></Table.Cell>
                        <Table.Cell>
                            <Box ml={5}>
                                <Text color='blue.600'>
                                    <Link onClick={_ => window.location.href = '/docs'}>/docs</Link>
                                </Text>
                            </Box>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Entwicklung:</Text></Table.Cell>
                        <Table.Cell>
                            <Box ml={5}>
                                <Text color='blue.600'>
                                    <Link onClick={_ => window.location.href = '/dev'}>/dev</Link>
                                </Text>
                            </Box>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><Text as='b'>Environment:</Text></Table.Cell>
                        <Table.Cell>
                            <Badge ml='5' mr={10} variant='surface' colorPalette='yellow'>{process.env.NODE_ENV}</Badge>
                        </Table.Cell>
                    </Table.Row>
                    {isRoot && (
                        <>
                            <Table.Row color='blue'>
                                <Table.Cell><Text as='b'>Organizations:</Text></Table.Cell>
                                <Table.Cell><Text ml='5' as='i'>{info?.organizationEntries ?? '⚙️'}</Text></Table.Cell>
                            </Table.Row>
                            <Table.Row color='blue'>
                                <Table.Cell><Text as='b'>User Entries:</Text></Table.Cell>
                                <Table.Cell><Text ml='5' as='i'>{info?.userEntries ?? '⚙️'}</Text></Table.Cell>
                            </Table.Row>
                            <Table.Row color='blue'>
                                <Table.Cell><Text as='b'>Timesheet Entries:</Text></Table.Cell>
                                <Table.Cell><Text ml='5' as='i'>{info?.timesheetEntries ?? '⚙️'} / {info?.timesheetUsers ?? '⚙️'} users</Text></Table.Cell>
                            </Table.Row>
                            <Table.Row color='blue'>
                                <Table.Cell><Text as='b'>Hostname:</Text></Table.Cell>
                                <Table.Cell>
                                    <Text ml='5' as='i'>{window.location.hostname}</Text>
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row color='blue'>
                                <Table.Cell><Text as='b'>S3 Objects:</Text></Table.Cell>
                                <Table.Cell><Text ml='5' as='i'>{info?.s3Objects ?? '⚙️'} / {info?.messages ?? '✉️'} messages</Text></Table.Cell>
                            </Table.Row>
                        </>
                    )}
                </Table.Body>
            </Table.Root>

            <Center m='2rem'><SeparatorText text='Exchange Rates Test' /></Center>

            <ExchangeRates base={"CHF"} all={["USD", "EUR", "GBP"]} />

            <Center m='2rem'><SeparatorText text='Test Components' /></Center>

            <Alert status='info' borderRadius='md' borderStartWidth="3px" borderStartColor="colorPalette.600">
                Use this section to explore and test how the components work.
            </Alert>

            <VStack mt='1rem' w='full' maxW='25rem' align='stretch' gap={4}>
                <Text>Test Progress Bar</Text>
                <Slider
                    label='Progress (%)'
                    showValue
                    min={0}
                    max={100}
                    step={1}
                    value={[progressValue]}
                    onValueChange={({ value }) => setProgressValue(value[0])}
                    colorPalette='teal'
                />
                <Box>{ProgressBarReact(progressValue / 100)}</Box>
            </VStack>

            <HStack mt='1rem'>
                <Text>Test Color Picker</Text>
                <ColorPicker value='#00AA00' onChange={(_) => { }} isReadOnly={false} />
            </HStack>
            {isRoot && (
                <>

                    <HStack mt='1rem' maxWidth='25rem'>
                        <Text>Test Date Picker</Text>
                        <ChakraDatePickerCompat
                            isClearable={true}
                            selected={selectedDate}
                            onChange={(date: Date | null) => setSelectedDate(date)}
                            dateFormat='dd.MM.yyyy'
                            locale={de}
                            showPopperArrow={true}
                        />
                    </HStack>
                    <HStack mt='1rem'>
                        <Text>Test Confirm Dialog</Text>
                        <Button colorPalette='teal' onClick={() => onOpen()}>Confirm</Button>
                        <ConfirmDialog heading='Is it ok?' isOpen={open} onClose={onClose} onConfirm={() => Promise.resolve()} />
                    </HStack>
                    <VStack mt='1rem' w='fit-content' align='start'>
                        <Text>Test Year Picker</Text>
                        <YearSelect isClearable={true} value={selectedYear} onChange={setSelectedYear} startYear={2023} endYear={2027} />
                        <p>Selected Year: {selectedYear}</p>
                        <Text>No range:</Text>
                        <YearSelect isClearable={false} value={selectedYear} onChange={setSelectedYear} />
                    </VStack>
                </>
            )}
        </Stack>
    )
}

export default Info
